import { Injectable } from '@angular/core';
import { CONSTANTS } from '../../Constants/Constants';
import { GuidedViewLaborSettings } from '../../models/dtos/Classes/GuidedViewLaborSettings';
import { LaborData } from '../../models/dtos/Classes/LaborData';
import { LaborLevelwiseData } from '../../models/dtos/Classes/LaborLevelwiseData';
import { CommonDataService } from '../common-data.service';
import { IndexDBService } from '../common/indexDB.service';
import { SolCalculationsService } from '../operations/sol-calculations.service';
import { TaskBasedLaborConstants } from '../../models/dtos';

/** Project dates reach this service either as Date objects or as serialized strings. */
type DateInput = Date | string | null;

export interface ProjectDates {
    startDate: DateInput;
    endDate: DateInput;
}

/** Monthly project management activities — the only base task driven by the project duration. */
const MONTHLY_ACTIVITIES_TASK_REFERENCE = 'T292';

@Injectable({
    providedIn: 'root'
})
export class ProjectManagementHoursUpdateService {
    
    private taskBasedLaborConstants: TaskBasedLaborConstants | null = null;

    constructor(
        private readonly commonDataService: CommonDataService,
        private readonly indexDBService: IndexDBService,
        private readonly solCalculationsService: SolCalculationsService
    ) { }


    /**
     * Updates the duration driven project management hours on the project level base tasks,
     * persists the updated labor data back to IndexedDB and re-runs the cost + sell price pipeline.
     *
     * When the previous dates are known the hours move only by the change in duration, so a save that
     * leaves the duration untouched never reprices PM labor, even if the stored hours were produced by
     * another source. Without usable previous dates the hours are set from the full duration.
     *
     * Callers that also reset escalation must await this first, so escalation is applied on top of
     * the updated labor costs.
     * Returns true when at least one task hour changed.
     */
    async updateHoursOnDurationChange(startDate: DateInput, endDate: DateInput, previousDates?: ProjectDates): Promise<boolean> {
        try {
            this.taskBasedLaborConstants = this.commonDataService.taskBasedLaborConstants;
            return await this.recalculateAndPersistHours(startDate, endDate, previousDates);
        } catch (error) {
            console.error('Failed to update project management hours after duration change:', error);
            return false;
        }
    }

    private async recalculateAndPersistHours(startDate: DateInput, endDate: DateInput, previousDates?: ProjectDates): Promise<boolean> {
        const projectGuid = this.commonDataService.getSQWebProject()?.itemUniqueId;

        if (!projectGuid || !this.indexDBService.isDatabaseReady()) {
            return false;
        }

        const laborData = await this.indexDBService.getLaborDataByGuid(projectGuid);
        const settings = laborData?.guidedViewLaborSettingsCollection;

        if (!settings?.isProjectManagementChecked) {
            return false;
        }

        const durationInMonths = this.getProjectDurationInMonths(startDate, endDate);
        const monthlyHoursChange = this.getMonthlyHoursChange(settings, durationInMonths, previousDates);
        const hoursDeltaByOffice = new Map<number, number>();

        for (const baseTask of laborData.baseTasksCollection ?? []) {
            if (baseTask?.referenceNumber !== MONTHLY_ACTIVITIES_TASK_REFERENCE) {
                continue;
            }

            const currentHours = this.toLaborHours(baseTask.projectManagementLabor);
            const hours = monthlyHoursChange === null
                ? this.getT292Hours(settings, durationInMonths)
                : Math.max(currentHours + monthlyHoursChange, CONSTANTS.ZERO);

            if (currentHours === hours) {
                continue;
            }

            baseTask.projectManagementLabor = hours;
            const delta = hours - currentHours;
            hoursDeltaByOffice.set(baseTask.salesOfficeId, (hoursDeltaByOffice.get(baseTask.salesOfficeId) ?? CONSTANTS.ZERO) + delta);
        }

        if (hoursDeltaByOffice.size === CONSTANTS.ZERO) {
            return false;
        }

        this.applyHoursDeltaToOvertimeAdjustments(laborData, hoursDeltaByOffice);

        const laborLevelwiseData: LaborLevelwiseData = { laborsCollection: { [projectGuid]: laborData } };
        await this.indexDBService.storeLaborData(laborLevelwiseData);
        await this.solCalculationsService.calculateCostsAndSellPricesWithCollections();

        return true;
    }

    /** Hours added (or removed) by moving from the previous duration to the new one; null when the previous dates are unusable. */
    private getMonthlyHoursChange(settings: GuidedViewLaborSettings, newDurationInMonths: number, previousDates?: ProjectDates): number | null {
        const hasPreviousDates = !!previousDates?.startDate && !!previousDates?.endDate
            && !Number.isNaN(new Date(previousDates.startDate).getTime())
            && !Number.isNaN(new Date(previousDates.endDate).getTime());

        if (!hasPreviousDates) {
            return null;
        }

        const previousDurationInMonths = this.getProjectDurationInMonths(previousDates.startDate, previousDates.endDate);
        return this.getT292Hours(settings, newDurationInMonths) - this.getT292Hours(settings, previousDurationInMonths);
    }

    /**
     * Labor cost is calculated from the OT/PWR adjustments, which hold the task hours split over
     * straight time and the overtime multipliers. The task hour change is applied to the straight
     * time row so any existing overtime distribution is preserved.
     */
    private applyHoursDeltaToOvertimeAdjustments(laborData: LaborData, hoursDeltaByOffice: Map<number, number>): void {
        for (const otPwrItem of laborData.stdSalesOfficesOtPwrAdjustmentsCollection ?? []) {
            const delta = hoursDeltaByOffice.get(otPwrItem.salesOfficeId);
            const straightTimeRow = otPwrItem.overTimePWRAdjustments?.[CONSTANTS.OT];

            if (!delta || !straightTimeRow) {
                continue;
            }

            straightTimeRow.projectManagementLabor = Math.max(
                this.toLaborHours(straightTimeRow.projectManagementLabor) + delta,
                CONSTANTS.ZERO
            );
        }
    }

    /** Mirrors the backend CalculateMonths: calendar month difference, plus one when end day >= start day. */
    getProjectDurationInMonths(startDate: DateInput, endDate: DateInput): number {
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
            return CONSTANTS.ZERO;
        }

        const monthsPerYear = 12;
        const months =
            end.getMonth() - start.getMonth() +
            monthsPerYear * (end.getFullYear() - start.getFullYear()) +
            (end.getDate() >= start.getDate() ? 1 : 0);

        return Math.max(months, CONSTANTS.ZERO);
    }

    /** Turnkey from office, applicable when a Field Project Lead is used. */
    getT288Hours(settings: GuidedViewLaborSettings): number {
        if (!this.isProjectManagementEnabled(settings) || !settings.useProjectLead) {
            return CONSTANTS.ZERO;
        }

        return settings.isInitialTurnkey ? this.taskBasedLaborConstants.projectManagerInitialTurnkeyCoordination : CONSTANTS.ZERO;
    }

    /** Turnkey from office, applicable when no Field Project Lead is used. */
    getT289Hours(settings: GuidedViewLaborSettings): number {
        if (!this.isProjectManagementEnabled(settings) || settings.useProjectLead) {
            return CONSTANTS.ZERO;
        }

        return settings.isInitialTurnkey ? this.taskBasedLaborConstants.projectManagerInitialTurnkeyCoordination : CONSTANTS.ZERO;
    }

    /** Subcontractor coordination visits. */
    getT290Hours(settings: GuidedViewLaborSettings): number {
        if (!this.isProjectManagementEnabled(settings) || settings.useProjectLead) {
            return CONSTANTS.ZERO;
        }

        const visits = this.toPositiveNumber(settings.onsiteSubcontractorVisits);

        return visits * this.taskBasedLaborConstants.onSiteSubcontractorCoordinationVisits;
    }

    /** Meetings / coordination visits. */
    getT291Hours(settings: GuidedViewLaborSettings): number {
        if (!this.isProjectManagementEnabled(settings) || settings.useProjectLead) {
            return CONSTANTS.ZERO;
        }

        const meetings = this.toPositiveNumber(settings.onsiteMeetings);

        return meetings * this.taskBasedLaborConstants.onSiteMeetingsOrCoordinationVisits;
    }

    /** On site duration when no Field Project Lead is used. */
    getT287Hours(settings: GuidedViewLaborSettings): number {
        if (!this.isProjectManagementEnabled(settings) || settings.useProjectLead) {
            return CONSTANTS.ZERO;
        }

        const visits = this.toPositiveNumber(settings.onsiteSubcontractorVisits);
        const meetings = this.toPositiveNumber(settings.onsiteMeetings);

        if (visits > CONSTANTS.ZERO && meetings > CONSTANTS.ZERO) {
            return (this.toPositiveNumber(settings.onsiteDuration) / CONSTANTS.TWENTY * CONSTANTS.FOUR) + this.taskBasedLaborConstants.noOnSiteCoordinationVisitsPlanned;
        }

        return CONSTANTS.ZERO;
    }

    /** On site duration when a Field Project Lead is used. */
    getT286Hours(settings: GuidedViewLaborSettings): number {
        if (!this.isProjectManagementEnabled(settings) || !settings.useProjectLead) {
            return CONSTANTS.ZERO;
        }

        return (this.toPositiveNumber(settings.onsiteDuration) / CONSTANTS.TWENTY * CONSTANTS.FOUR) + this.taskBasedLaborConstants.projectLeadRemoteManagement;
    }

    /** Project duration driven hours. */
    getT292Hours(settings: GuidedViewLaborSettings, durationInMonths: number): number {
        if (!this.isProjectManagementEnabled(settings)) {
            return CONSTANTS.ZERO;
        }

        return this.toPositiveNumber(durationInMonths) * this.taskBasedLaborConstants.projectManagerOrProjectLeadMonthlyActivities;
    }

    /** Delivery of submittal package. */
    getT293Hours(settings: GuidedViewLaborSettings): number {
        if (!this.isProjectManagementEnabled(settings)) {
            return CONSTANTS.ZERO;
        }

        return settings.isSubmittalPackageDelivery ? this.taskBasedLaborConstants.deliveryOfSubmittalPackageToAHJRequired : CONSTANTS.ZERO;
    }

    /** Sales to Ops & PM to service turnovers. */
    getT294Hours(settings: GuidedViewLaborSettings): number {
        if (!this.isProjectManagementEnabled(settings)) {
            return CONSTANTS.ZERO;
        }

        return this.toPositiveNumber(settings.serviceTurnovers) > CONSTANTS.ZERO ? CONSTANTS.ZERO : this.taskBasedLaborConstants.designCoordination;
    }

    private isProjectManagementEnabled(settings: GuidedViewLaborSettings): boolean {
        return !!settings?.isProjectManagementChecked;
    }

    private toPositiveNumber(value: number): number {
        return Number.isFinite(value) ? Math.max(value, CONSTANTS.ZERO) : CONSTANTS.ZERO;
    }

    /** Labor hours carry a sentinel for "not applicable", which counts as no hours. */
    private toLaborHours(value: number): number {
        return value === CONSTANTS.NA_LABOR_VALUE ? CONSTANTS.ZERO : this.toPositiveNumber(value);
    }
}